import { PositionHistoryItem, PositionItem } from '@libs/api-reference';
import { DeductionsStoreType } from '@core/symbol';

import { POSITIONS_SIDE } from '../../constants';
import { isPositionBuy as isContractBuy, isPositionBuy } from '../positionType';

type ValueOf<K> = K[keyof K];

export type PositionItemWithPrices = ReturnType<typeof getPositionSymbolValues>;
export type PositionHistoryItemWithPrices = PositionHistoryItem &
  ReturnType<typeof getPositionSymbolValues>;

const SELL_TAX_RATE = 0.005; // should delete

type TPositionArg = Pick<
  PositionItem,
  | 'netAveragePrice'
  | 'quantity'
  | 'lastTradePrice'
  | 'contractSize'
  | 'underlyingAssetLastTradePrice'
  | 'sellTaxRate'
  | 'physicalSettlementFee'
  | 'strikePrice'
>;

type CalcQuantityParamsType = {
  totalValue?: number | null;
  price?: number | null;
};

type TCalcOptionValue = Pick<
  TPositionArg,
  'quantity' | 'lastTradePrice' | 'contractSize'
>;

type TCalcOptionReturnValue = TCalcOptionValue &
  Pick<TPositionArg, 'netAveragePrice'>;

type TCalcOptionReturnValuePercentage = Omit<
  TCalcOptionReturnValue,
  'contractSize' | 'quantity'
>;

type TCalcContractReturnValueParams = Pick<
  PositionItem,
  'cashSettlementFee' | 'strikePrice' | 'underlyingAsseClosingPrice'
>;

type TCalcCashSettlementRequired = Pick<
  PositionItem,
  | 'strikePrice'
  | 'underlyingAsseClosingPrice'
  | 'contractSize'
  | 'cashSettlementFee'
  | 'quantity'
>;

type TCalcPhysicalSettlementRequired = Pick<
  PositionItem,
  'strikePrice' | 'contractSize' | 'physicalSettlementFee' | 'quantity'
>;

type TCalcOptionCostPrice = Omit<TCalcOptionReturnValue, 'lastTradePrice'>;

type TCalcOptionStrikePrice = Omit<TPositionArg, 'lastTradePrice'> & {
  contractType: ValueOf<typeof POSITIONS_SIDE>;
};

type TCalcOptionPositionValues = TPositionArg & {
  contractType: ValueOf<typeof POSITIONS_SIDE>;
  positionSide: ValueOf<typeof POSITIONS_SIDE>;
};

function calcOptionSymbolQuantityBuy(
  { totalValue, price }: CalcQuantityParamsType,
  wage: DeductionsStoreType,
  roundUp = false,
  contractSize: number
): number {
  if (totalValue && price && contractSize) {
    const quantity = totalValue / (price * (1 + wage.buyValue));

    return roundUp
      ? Math.floor(quantity / contractSize)
      : Math.ceil(quantity / contractSize);
  }

  return 0;
}

function calcOptionSymbolQuantitySell(
  { totalValue, price }: CalcQuantityParamsType,
  wage: DeductionsStoreType,
  roundUp = false,
  contractSize: number
): number {
  if (totalValue && price && contractSize) {
    const quantity = totalValue / (price * (1 - wage.sellValue));

    return roundUp
      ? Math.floor(quantity / contractSize)
      : Math.ceil(quantity / contractSize);
  }

  return 0;
}

function calcOptionSymbolBuyBreakPointPrice(
  netAveragePrice: number,
  wage: DeductionsStoreType
): number {
  return (netAveragePrice * (1 + wage.buyValue)) / (1 - wage.sellValue);
}

function calcOptionSymbolSellBreakPointPrice(
  netAveragePrice: number,
  wage: DeductionsStoreType
): number {
  return (netAveragePrice * (1 - wage.sellValue)) / (1 + wage.buyValue);
}

function calcSymbolSellValueOption(
  { quantity, lastTradePrice, contractSize }: TCalcOptionValue,
  wage: DeductionsStoreType
): number {
  return quantity * contractSize * lastTradePrice * (1 - wage.sellValue);
}

function calcSymbolBuyValueOption(
  { quantity, lastTradePrice, contractSize }: TCalcOptionValue,
  wage: DeductionsStoreType
): number {
  return quantity * contractSize * lastTradePrice * (1 + wage.buyValue);
}

// ------------------------------
// (بازده) مقدار سود و زیان مانده - ✓
// ------------------------------
function calcOptionBuyReturnValue(
  {
    quantity,
    lastTradePrice,
    contractSize,
    netAveragePrice,
  }: TCalcOptionReturnValue,
  wage: DeductionsStoreType
): number {
  return (
    quantity *
    contractSize *
    (lastTradePrice * (1 - wage.sellValue) -
      netAveragePrice * (1 + wage.buyValue))
  );
}

function calcOptionSellReturnValue(
  {
    quantity,
    lastTradePrice,
    contractSize,
    netAveragePrice,
  }: TCalcOptionReturnValue,
  wage: DeductionsStoreType
): number {
  return (
    quantity *
    contractSize *
    (netAveragePrice * (1 - wage.sellValue) -
      lastTradePrice * (1 + wage.buyValue))
  );
}

function calcOptionBuyPercentageReturnValue(
  { lastTradePrice, netAveragePrice }: TCalcOptionReturnValuePercentage,
  wage: DeductionsStoreType
): number {
  return (
    ((lastTradePrice * (1 - wage.sellValue) -
      netAveragePrice * (1 + wage.buyValue)) /
      (netAveragePrice * (1 + wage.buyValue))) *
    100
  );
}

function calcOptionSellPercentageReturnValue(
  { lastTradePrice, netAveragePrice }: TCalcOptionReturnValuePercentage,
  wage: DeductionsStoreType
): number {
  return (
    ((netAveragePrice * (1 - wage.sellValue) -
      lastTradePrice * (1 + wage.buyValue)) /
      (netAveragePrice * (1 - wage.sellValue))) *
    100
  );
}

function calcOptionBuyCostPrice(
  { quantity, contractSize, netAveragePrice }: TCalcOptionCostPrice,
  wage: DeductionsStoreType
): number {
  return quantity * netAveragePrice * (1 + wage.buyValue) * contractSize;
}

function calcOptionSellCostPrice(
  { quantity, contractSize, netAveragePrice }: TCalcOptionCostPrice,
  wage: DeductionsStoreType
): number {
  return quantity * netAveragePrice * (1 - wage.sellValue) * contractSize;
}

function calcBuyStrikeReturnValue(
  {
    quantity,
    contractSize,
    netAveragePrice,
    underlyingAssetLastTradePrice,
    strikePrice,
    contractType,
    physicalSettlementFee,
  }: TCalcOptionStrikePrice,
  wage: DeductionsStoreType
): number {
  if (isContractBuy(contractType)) {
    return (
      quantity *
      contractSize *
      (underlyingAssetLastTradePrice * (1 - wage.sellValue) -
        strikePrice * (1 + physicalSettlementFee) -
        netAveragePrice * (1 + wage.buyValue))
    );
  }

  return (
    quantity *
    contractSize *
    (strikePrice * (1 + physicalSettlementFee) * (1 + SELL_TAX_RATE) -
      underlyingAssetLastTradePrice * (1 - wage.sellValue) -
      netAveragePrice * (1 + wage.buyValue))
  );
}

function calcSellStrikeReturnValue(
  {
    quantity,
    contractSize,
    netAveragePrice,
    underlyingAssetLastTradePrice,
    strikePrice,
    contractType,
    // sellTaxRate,
    physicalSettlementFee,
  }: TCalcOptionStrikePrice,
  wage: DeductionsStoreType
): number {
  if (isContractBuy(contractType)) {
    return (
      quantity *
      contractSize *
      (strikePrice * (1 + physicalSettlementFee) * (1 + SELL_TAX_RATE) -
        underlyingAssetLastTradePrice * (1 + wage.buyValue) +
        netAveragePrice * (1 - wage.sellValue))
    );
  }

  return (
    quantity *
    contractSize *
    (underlyingAssetLastTradePrice * (1 + wage.buyValue) -
      (strikePrice * (1 + physicalSettlementFee) +
        netAveragePrice * (1 - wage.sellValue)))
  );
}

function calcSymbolValues(
  {
    lastTradePrice,
    netAveragePrice: pureNetAveragePrice,
    quantity,
    contractSize,
    positionSide,
    contractType,
    underlyingAssetLastTradePrice,
    sellTaxRate,
    physicalSettlementFee,
    strikePrice,
  }: TCalcOptionPositionValues,
  wage: DeductionsStoreType
) {
  const netAveragePrice = parseFloat(pureNetAveragePrice.toFixed(7));

  const isBuySide = isPositionBuy(positionSide);

  let value = 0;
  let costPrice = 0;
  let breakPoint = 0;
  let returnValue = 0;
  let strikeReturnValue = 0;
  let returnValuePercentage = 0;

  if (isBuySide) {
    value = calcSymbolBuyValueOption(
      { quantity, lastTradePrice, contractSize },
      wage
    );
    costPrice = calcOptionBuyCostPrice(
      {
        quantity,
        netAveragePrice,
        contractSize,
      },
      wage
    );
    breakPoint = calcOptionSymbolBuyBreakPointPrice(netAveragePrice, wage);
    returnValue = calcOptionBuyReturnValue(
      { quantity, lastTradePrice, contractSize, netAveragePrice },
      wage
    );
    strikeReturnValue = calcBuyStrikeReturnValue(
      {
        quantity,
        contractSize,
        netAveragePrice,
        underlyingAssetLastTradePrice,
        strikePrice,
        physicalSettlementFee,
        sellTaxRate,
        contractType,
      },
      wage
    );
    returnValuePercentage = calcOptionBuyPercentageReturnValue(
      { netAveragePrice, lastTradePrice },
      wage
    );
  } else {
    value = calcSymbolSellValueOption(
      { quantity, lastTradePrice, contractSize },
      wage
    );
    costPrice = calcOptionSellCostPrice(
      {
        quantity,
        netAveragePrice,
        contractSize,
      },
      wage
    );
    breakPoint = calcOptionSymbolSellBreakPointPrice(netAveragePrice, wage);
    returnValue = calcOptionSellReturnValue(
      { quantity, lastTradePrice, contractSize, netAveragePrice },
      wage
    );
    strikeReturnValue = calcSellStrikeReturnValue(
      {
        quantity,
        contractSize,
        netAveragePrice,
        underlyingAssetLastTradePrice,
        strikePrice,
        physicalSettlementFee,
        sellTaxRate,
        contractType,
      },
      wage
    );
    returnValuePercentage = calcOptionSellPercentageReturnValue(
      { netAveragePrice, lastTradePrice },
      wage
    );
  }

  return {
    value,
    costPrice,
    breakPoint,
    returnValue,
    netAveragePrice,
    strikeReturnValue,
    returnValuePercentage,
  };
}

const getPositionSymbolValues = (
  symbol: PositionItem,
  wage: DeductionsStoreType
) => {
  const {
    quantity,
    lastTradePrice,
    netAveragePrice,
    contractSize,
    positionSide,
    contractType,
    underlyingAssetLastTradePrice,
    sellTaxRate,
    physicalSettlementFee,
    strikePrice,
  } = symbol;

  return {
    ...symbol,
    ...calcSymbolValues(
      {
        quantity,
        lastTradePrice,
        netAveragePrice,
        contractSize,
        positionSide: positionSide as TCalcOptionPositionValues['positionSide'],
        contractType: contractType as TCalcOptionPositionValues['contractType'],
        underlyingAssetLastTradePrice,
        strikePrice,
        sellTaxRate,
        physicalSettlementFee,
      },
      wage
    ),
  };
};

function calcBuyContractReturnValue({
  strikePrice,
  cashSettlementFee,
  underlyingAsseClosingPrice,
}: TCalcContractReturnValueParams) {
  return underlyingAsseClosingPrice - strikePrice * (1 + cashSettlementFee);
}

function calcSellContractReturnValue({
  strikePrice,
  cashSettlementFee,
  underlyingAsseClosingPrice,
}: TCalcContractReturnValueParams) {
  return (1 + cashSettlementFee) * strikePrice - underlyingAsseClosingPrice;
}

function calcCashSettlementRequired({
  strikePrice,
  underlyingAsseClosingPrice,
  contractSize,
  cashSettlementFee,
  quantity,
}: TCalcCashSettlementRequired) {
  return (
    Math.abs(strikePrice - underlyingAsseClosingPrice) *
    contractSize *
    (1 + cashSettlementFee) *
    quantity
  );
}

function calcPhysicalSettlementRequired({
  strikePrice,
  contractSize,
  physicalSettlementFee,
  quantity,
}: TCalcPhysicalSettlementRequired) {
  return strikePrice * contractSize * (1 + physicalSettlementFee) * quantity;
}

export {
  getPositionSymbolValues,
  calcBuyContractReturnValue,
  calcSellContractReturnValue,
  calcSymbolSellValueOption,
  calcSymbolBuyValueOption,
  calcCashSettlementRequired,
  calcPhysicalSettlementRequired,
  calcOptionSymbolQuantityBuy,
  calcOptionSymbolQuantitySell,
};
